import * as React from 'react'
import { useDeepCompareEffect } from 'use-deep-compare'

import {
  BROWSER_ERROR_KEYS,
  BrowserErrors,
  instrumentBrowserError,
} from '@library/observability/browser'

export enum ERROR_COMPONENT_MODES {
  'ON_RENDER' = 'on render',
  'MANUAL' = 'manual',
}

type OnRenderModeProps<K extends BROWSER_ERROR_KEYS> = {
  children: React.ReactNode
  errorKey: K
  mode: ERROR_COMPONENT_MODES.ON_RENDER
} & typeof BrowserErrors[K]['detail']

type ManualModeProps<K extends BROWSER_ERROR_KEYS> = {
  children: React.ReactNode
  errorKey: K
  mode: ERROR_COMPONENT_MODES.MANUAL
  shouldSendError: boolean
} & typeof BrowserErrors[K]['detail']

type InstrumentedErrorComponentProps<K extends BROWSER_ERROR_KEYS> =
  | OnRenderModeProps<K>
  | ManualModeProps<K>

type MinusBaseProps<P extends { children: unknown; mode: unknown; errorKey: unknown }> = Omit<
  P,
  'mode' | 'errorKey' | 'children'
>

export function InstrumentedErrorComponent<T extends BROWSER_ERROR_KEYS>(
  props: InstrumentedErrorComponentProps<T>,
) {
  const { children, mode, errorKey, ...rest } = props

  useDeepCompareEffect(() => {
    if (mode === ERROR_COMPONENT_MODES.MANUAL) {
      const { shouldSendError, ...errorDetail } = rest as MinusBaseProps<ManualModeProps<T>>

      if (shouldSendError) {
        instrumentBrowserError(errorKey, errorDetail)
      }
    } else {
      const errorDetail = rest as MinusBaseProps<OnRenderModeProps<T>>

      instrumentBrowserError(errorKey, errorDetail)
    }
  }, [mode, errorKey, rest])

  return <>{children}</>
}
